"use client";

import { useEffect, useRef, type HTMLAttributes, type ReactNode } from "react";
import { formatMetric } from "@/lib/catalog-format";
import type { Metric } from "@/lib/comparison-catalog";
import { formatCurrency, type Locale } from "@/lib/i18n";

const reducedMotion = () => typeof window === "undefined" || typeof IntersectionObserver === "undefined" || window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Entrance motion, once. The CSS animation (v2-catalog.css, `.vc-grow` / `.vc-pop`) already plays
 * when the chart first paints, so charts in view animate with no JS and render complete without it.
 * A chart that is still below the fold on mount is parked (`data-reveal="wait"`) and replayed when
 * it scrolls in (`data-reveal="go"`). Reduced motion: nothing moves.
 */
export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element || reducedMotion() || element.getBoundingClientRect().top < window.innerHeight * 0.9) return;
    element.setAttribute("data-reveal", "wait");
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      element.setAttribute("data-reveal", "go");
      observer.disconnect();
    }, { rootMargin: "0px 0px -12% 0px" });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return ref;
}

export function Reveal({ children, ...rest }: HTMLAttributes<HTMLDivElement> & { children: ReactNode }) {
  const ref = useReveal<HTMLDivElement>();
  return <div ref={ref} {...rest}>{children}</div>;
}

/** A figure that counts up once when it scrolls into view. The final value is server-rendered, and
 * screen readers only ever get the final value. */
export function Ticker({ value, metric, locale = "es", className }: { value: number; metric?: Metric; locale?: Locale; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const text = (n: number) => metric ? formatMetric(n, metric, locale) : formatCurrency("en", n);
  const final = text(value);
  useEffect(() => {
    const element = ref.current;
    if (!element || reducedMotion() || !Number.isFinite(value) || value === 0) return;
    const format = (n: number) => metric ? formatMetric(n, metric, locale) : formatCurrency("en", n);
    let frame = 0;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      const start = performance.now();
      const step = (now: number) => {
        const progress = Math.min(1, (now - start) / 1100);
        element.textContent = format(value * (1 - (1 - progress) ** 4));
        if (progress < 1) frame = requestAnimationFrame(step);
      };
      frame = requestAnimationFrame(step);
    }, { threshold: 0.6 });
    observer.observe(element);
    return () => { observer.disconnect(); cancelAnimationFrame(frame); element.textContent = format(value); };
  }, [value, metric, locale]);
  return <span className={className}><span ref={ref} aria-hidden="true">{final}</span><span className="ui-sr-only">{final}</span></span>;
}
